import { OrbitControls } from "@react-three/drei";
import gsap from 'gsap';
import React, { useEffect, useRef } from "react";

const CameraWithAnimation = ({ hasStarted, sections, currentSection, onSectionChange, isMobile }) => {
  const controlsRef = useRef();
  const isTransitioning = useRef(false);
  const timelineRef = useRef();

  const section = sections[currentSection];

  // Initial setup for camera and target positions
  useEffect(() => {
    if (controlsRef.current && !isMobile) {
      const initialSection = sections[0];
      
      controlsRef.current.object.position.set(
        initialSection.orbit.camera.x,
        initialSection.orbit.camera.y,
        initialSection.orbit.camera.z
      );
      
      controlsRef.current.target.set(
        initialSection.orbit.target.x,
        initialSection.orbit.target.y,
        initialSection.orbit.target.z
      );
      
      controlsRef.current.update();
    }
  }, [controlsRef.current, sections, isMobile]);

  // Handle transition from INITIAL to DEFAULT section when experience starts
  useEffect(() => {
    if (hasStarted && controlsRef.current && !isMobile) {
      const defaultSection = sections[2]; // Changed to index 2 for the DEFAULT section
      
      if (timelineRef.current) {
        timelineRef.current.kill();
      }

      timelineRef.current = gsap.timeline({
        onComplete: () => {
          isTransitioning.current = false;
          onSectionChange(2); // Changed to index 2
        }
      });

      timelineRef.current.to(controlsRef.current.target, {
        x: defaultSection.orbit.target.x,
        y: defaultSection.orbit.target.y,
        z: defaultSection.orbit.target.z,
        duration: 2.5,
        ease: "power2.inOut"
      });

      timelineRef.current.to(controlsRef.current.object.position, {
        x: defaultSection.orbit.camera.x,
        y: defaultSection.orbit.camera.y,
        z: defaultSection.orbit.camera.z,
        duration: 2.5,
        ease: "power2.inOut"
      }, "<");
    }
  }, [hasStarted, sections, onSectionChange, isMobile]);

  // Handle section changes
  useEffect(() => {
    if (isTransitioning.current || !controlsRef.current || isMobile) return;
    
    const section = sections[currentSection];
    if (!section) return;
    
    isTransitioning.current = true;
    
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    timelineRef.current = gsap.timeline({
      onComplete: () => {
        isTransitioning.current = false;
      }
    });

    timelineRef.current.to(controlsRef.current.target, {
      x: section.orbit.target.x,
      y: section.orbit.target.y,
      z: section.orbit.target.z,
      duration: 1.5,
      ease: "power2.inOut"
    });

    timelineRef.current.to(controlsRef.current.object.position, {
      x: section.orbit.camera.x,
      y: section.orbit.camera.y,
      z: section.orbit.camera.z,
      duration: 1.5,
      ease: "power2.inOut"
    }, "<");

    timelineRef.current.add(() => {
      controlsRef.current.update();
    });
  }, [currentSection, sections, isMobile]);

  return (
    <>
      {!isMobile && (
        <OrbitControls
          ref={controlsRef}
          enablePan={true}
          enableZoom={false}
          minDistance={5}
          enabled={!isMobile}
          enableDamping={true}
          dampingFactor={0.05}
          rotateSpeed={0.3}
          maxDistance={100}
          minPolarAngle={0}
          maxPolarAngle={Math.PI / 2}
        />
      )}
    </>
  );
};

export default CameraWithAnimation;
